import React, { useState, useEffect, useRef } from 'react';
import API from '../utils/api';
import { getFileUrl } from '../utils/fileUtils';
import { Link, useSearchParams } from 'react-router-dom';
import { FaBuilding, FaMapMarkerAlt, FaUsers, FaCalendar, FaGlobe, FaLinkedin, FaTwitter, FaFacebook, FaSearch, FaFilter, FaArrowRight, FaChevronDown } from 'react-icons/fa';
import FAQSection from '../components/FAQSection';
import './Companies.css';

// ── CUSTOM DROPDOWN (Functionality intact, updated UI) ────────────────
const CustomDropdown = ({ options, value, onChange, icon: Icon, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="custom-dropdown-btn"
      >
        <div className="dropdown-icon-left"><Icon /></div>
        {selectedOption ? selectedOption.label : placeholder}
        <FaChevronDown className={`dropdown-icon-right ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="custom-dropdown-menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`custom-dropdown-option ${value === opt.value ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};



// ── STATIC DATA (UNCHANGED) ───────────────────────────────────────────
const industryOptions = [
  { value: '', label: 'All Industries' },
  { value: 'IT', label: 'IT' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Education', label: 'Education' }
];

const sizeOptions = [
  { value: '', label: 'Any Size' },
  { value: '1-50', label: '1-50 Employees' },
  { value: '51-200', label: '51-200 Employees' },
  { value: '201-500', label: '201-500 Employees' },
  { value: '501-1000', label: '501-1000 Employees' },
  { value: '1000+', label: '1000+ Employees' }
];

const Companies = () => {
  // ── STATE (UNCHANGED) ───────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterSize, setFilterSize] = useState('');

  // ── EFFECTS & HANDLERS (UNCHANGED) ──────────────────────────────────
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchTerm(urlSearch);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchAllCompanies();
  }, []);

  useEffect(() => {
    filterCompanies();
  }, [searchTerm, filterIndustry, filterSize, allCompanies]);

  const fetchAllCompanies = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/companies');
      setAllCompanies(data);
      filterCompanies();
    } catch (error) {
      console.error('Error fetching companies:', error);
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = allCompanies;
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(company =>
        company.name.toLowerCase().includes(search) ||
        company.description.toLowerCase().includes(search) ||
        (company.industry && company.industry.toLowerCase().includes(search))
      );
    }
    if (filterIndustry) {
      filtered = filtered.filter(company => company.industry === filterIndustry);
    }
    if (filterSize) {
      filtered = filtered.filter(company => company.companySize === filterSize);
    }
    setCompanies(filtered);
    setLoading(false);
  };

  return (
    <div className="companies-page">
      {/* ===================== HERO SECTION ===================== */}
      <section className="comp-hero">
        <div className="comp-hero-bg-pattern"></div>
        <div className="comp-hero-glow-1"></div>
        <div className="comp-hero-glow-2"></div>
        
        <div className="comp-hero-container">
          <div className="comp-hero-text">
            <div className="comp-hero-badge">
              <FaBuilding size={12} /> Top Employers
            </div>
            <h1 className="comp-hero-title">
              Discover Great Companies
            </h1>
            <p className="comp-hero-desc">
              Find your next workplace among industry leaders. Explore company cultures, open positions, and employee benefits.
            </p>
          </div>

          {/* New Hero SVG Illustration */}
          <div className="comp-hero-svg-wrapper">
            <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="200" cy="150" r="120" fill="rgba(255,255,255,0.1)" />
              <circle cx="200" cy="150" r="90" fill="rgba(255,255,255,0.05)" />
              {/* Buildings */}
              <rect x="120" y="100" width="60" height="150" rx="4" fill="rgba(255,255,255,0.8)" />
              <rect x="190" y="140" width="50" height="110" rx="4" fill="rgba(255,255,255,0.6)" />
              <rect x="250" y="80" width="70" height="170" rx="4" fill="rgba(255,255,255,0.95)" />
              {/* Windows */}
              <rect x="135" y="120" width="10" height="10" rx="2" fill="#00c2ab" />
              <rect x="155" y="120" width="10" height="10" rx="2" fill="#00c2ab" />
              <rect x="135" y="140" width="10" height="10" rx="2" fill="#00c2ab" />
              <rect x="155" y="140" width="10" height="10" rx="2" fill="#00c2ab" />
              <rect x="265" y="100" width="12" height="12" rx="2" fill="#00c2ab" />
              <rect x="285" y="100" width="12" height="12" rx="2" fill="#00c2ab" />
              <rect x="265" y="125" width="12" height="12" rx="2" fill="#00c2ab" />
              <rect x="285" y="125" width="12" height="12" rx="2" fill="#00c2ab" />
              {/* Ground */}
              <path d="M80 250 H340" stroke="white" strokeWidth="4" strokeLinecap="round" />
              {/* Floating Stars */}
              <path d="M100 80 L105 90 L115 95 L105 100 L100 110 L95 100 L85 95 L95 90 Z" fill="rgba(255,255,255,0.7)" />
              <path d="M320 50 L323 57 L330 60 L323 63 L320 70 L317 63 L310 60 L317 57 Z" fill="rgba(255,255,255,0.8)" />
            </svg>
          </div>
        </div>

        <div className="comp-hero-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,30 C320,60 1120,0 1440,30 L1440,60 L0,60 Z" fill="#f0f9f8" />
          </svg>
        </div>
      </section>

      {/* ===================== SEARCH & FILTERS ===================== */}
      <section className="comp-search-bar">
        <div className="comp-search-inner">
          <div className="comp-search-input-group">
            <FaSearch className="comp-search-icon" />
            <input
              type="text"
              placeholder="Search companies by name or keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="comp-search-input"
            />
          </div>

          <div className="comp-filters">
            <CustomDropdown 
              options={industryOptions} 
              value={filterIndustry} 
              onChange={setFilterIndustry} 
              icon={FaFilter} 
              placeholder="All Industries"
            />
            <CustomDropdown 
              options={sizeOptions} 
              value={filterSize} 
              onChange={setFilterSize} 
              icon={FaUsers} 
              placeholder="Any Size"
            />
          </div>
        </div>
      </section>

      {/* ===================== COMPANIES GRID ===================== */}
      <section className="comp-grid-section">
        {loading ? (
          <div className="comp-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00c2ab]"></div>
            <p style={{marginTop: '16px', color: '#64748b'}}>Loading companies...</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="comp-empty">
            <div className="comp-empty-icon">
              <FaBuilding />
            </div>
            <h3>No Companies Found</h3>
            <p>Try adjusting your filters or search term to find what you're looking for.</p>
          </div>
        ) : (
          <div className="comp-grid">
            {companies.map((company) => (
              <div key={company._id} className="comp-card">
                
                <div className="comp-card-header">
                  <div className="comp-logo-box">
                    {company.logo ? (
                      <img 
                        src={getFileUrl(company.logo)} 
                        alt={company.name} 
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/64?text=Logo'; }}
                      />
                    ) : (
                      <FaBuilding className="comp-logo-fallback" />
                    )}
                  </div>
                  <div>
                    <h3 className="comp-title">{company.name}</h3>
                    <span className="comp-industry-badge">
                      {company.industry || 'Tech'}
                    </span>
                  </div>
                </div>

                <p className="comp-desc">
                  {company.description || 'Discover our work culture and open positions. Join us in building the future.'}
                </p>

                <div className="comp-stats-grid">
                  <div className="comp-stat-item">
                    <FaMapMarkerAlt className="comp-stat-icon" />
                    <span className="comp-stat-text">
                      {company.location 
                        ? (typeof company.location === 'object' 
                            ? [company.location.city, company.location.state].filter(Boolean).join(', ') 
                            : company.location) 
                        : 'Multiple Locations'}
                    </span>
                  </div>
                  <div className="comp-stat-item">
                    <FaUsers className="comp-stat-icon" />
                    <span className="comp-stat-text">{company.companySize || '50-200'} Emp.</span>
                  </div>
                  <div className="comp-stat-item" style={{gridColumn: '1 / -1'}}>
                    <FaCalendar className="comp-stat-icon" />
                    <span className="comp-stat-text">Est. {company.foundedYear || '2020'}</span>
                  </div>
                </div>

                <Link
                  to={`/companies/${company._id}`}
                  className="comp-view-btn"
                >
                  View Profile <FaArrowRight />
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ===================== FAQ SECTION ===================== */}
      <FAQSection />
      
    </div>
  );
};

export default Companies;
